import { useEffect, useMemo, useState } from 'react';

import { parseServerError } from 'api/helpers';
import { LegalClientBodyEntity, LegalClientBodyType } from 'api/types/entity';
import { ConfirmationDialog } from 'components';
import { useFormContext } from 'libs/form/useFormContext';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { IconButton } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { Icon } from 'libs/ui/Icon';
import {
  DialogDeleteAndReasonChange,
  DialogReasonChange,
  Section,
} from 'modules/client/common/components';

import { BodiesInfoCard, BodyItem } from './BodiesInfoCard';
import { BodyDialog } from './BodyDialog/BodyDialog';

export interface CompanyStructureSectionProps {
  bodies: LegalClientBodyEntity[];
  onAddBody?: (
    body: { name: string; type: LegalClientBodyType; otherType?: string },
    reason: string
  ) => Promise<void>;
  onUpdateBody?: (
    id: string,
    body: { name: string; type: LegalClientBodyType; otherType?: string },
    reason: string
  ) => Promise<void>;
  onDeleteBody?: (id: string, reason: string) => Promise<void>;
  onArchiveBody?: (id: string, reason: string) => Promise<void>;
  onDearchiveBody?: (id: string, reason: string) => Promise<void>;
}

export const CompanyStructureSection = ({
  bodies,
  onDeleteBody,
  onArchiveBody,
  onDearchiveBody,
  onAddBody,
  onUpdateBody,
}: CompanyStructureSectionProps) => {
  const { t } = useTranslation();
  const notify = useNotify();

  const { readOnly } = useFormContext();

  const [activeBodyItem, setActiveBodyItem] = useState<BodyItem>();

  const [error, setError] = useState<{ title: string; subtitle: string }>();

  const {
    isOpen: isOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
    open: openDeleteDialog,
  } = useDialog();
  const {
    isOpen: isOpenArchiveDialog,
    onClose: onCloseArchiveDialog,
    open: openArchiveDialog,
  } = useDialog();

  const {
    isOpen: isOpenBodyDialog,
    onClose: onCloseBodyDialog,
    open: openBodyDialog,
  } = useDialog();

  const {
    isOpen: isOpenErrorDialog,
    onClose: onCloseErrorDialog,
    open: openErrorDialog,
  } = useDialog();

  const onClickDelete = onDeleteBody
    ? (row: BodyItem) => {
        setActiveBodyItem(row);
        openDeleteDialog();
      }
    : undefined;

  const onClickArchive = onArchiveBody
    ? (row: BodyItem) => {
        setActiveBodyItem(row);
        openArchiveDialog();
      }
    : undefined;

  const onClickAddBody = () => {
    setActiveBodyItem(undefined);
    openBodyDialog();
  };

  const onClickEdit = (row: BodyItem) => {
    setActiveBodyItem(row);
    openBodyDialog();
  };

  const [dialogLoading, setDialogLoading] = useState(false);

  const handleError = (title: string, error?: unknown) => {
    const serverError = parseServerError(error);
    if (serverError && serverError.localizedDescription) {
      setError({
        title,
        subtitle: serverError.localizedDescription,
      });
      openErrorDialog();
    } else {
      notify.error(error);
    }
  };

  const onDelete = async (reason: string) => {
    if (activeBodyItem && onDeleteBody) {
      setDialogLoading(true);
      try {
        await onDeleteBody(activeBodyItem.id, reason);
      } catch (error) {
        handleError(t('components.confirmDelete.title'), error);
      }
      setDialogLoading(false);
    }
    onCloseDeleteDialog();
  };

  const handleBodySubmit = async (
    body: { type: LegalClientBodyType; otherType?: string; name: string },
    reason: string
  ) => {
    setDialogLoading(true);

    if (!activeBodyItem) {
      if (onAddBody) {
        try {
          await onAddBody(body, reason);
        } catch (error) {
          /* empty */
        }
      }
    } else if (onUpdateBody) {
      try {
        await onUpdateBody(activeBodyItem.id, body, reason);
      } catch (error) {
        /* empty */
      }
    }
    onCloseBodyDialog();
    setDialogLoading(false);
  };

  const onArchiveAndDearchive = async (reason: string) => {
    if (activeBodyItem) {
      setDialogLoading(true);
      if (!activeBodyItem.archived && onArchiveBody) {
        try {
          await onArchiveBody(activeBodyItem.id, reason);
        } catch (error) {
          handleError(t('client.legal.companyStructure.moveArchive'), error);
        }
      }

      if (activeBodyItem.archived && onDearchiveBody) {
        try {
          await onDearchiveBody(activeBodyItem.id, reason);
        } catch (error) {
          /* empty */
        }
      }

      onCloseArchiveDialog();
      setDialogLoading(false);
    }
  };

  const items: BodyItem[] = useMemo(() => {
    const sortBodies = [...bodies].sort((a, b) =>
      a.archived === b.archived
        ? b.createdAt.getTime() - a.createdAt.getTime()
        : a.archived
        ? 1
        : -1
    );
    const result = sortBodies.map((h) => ({
      ...h,
      archived: !!h.archived,
    }));

    return result;
  }, [bodies]);

  useEffect(() => {
    if (!isOpenArchiveDialog && !isOpenBodyDialog && !isOpenDeleteDialog) {
      setActiveBodyItem(undefined);
    }
  }, [isOpenArchiveDialog, isOpenBodyDialog, isOpenDeleteDialog]);

  return (
    <Section
      datax="companyStructureSection"
      headerRight={
        !readOnly &&
        !!onAddBody && (
          <IconButton
            icon={<Icon.Plus size={16} />}
            mode="outlined"
            sizeVariant="xs"
            onClick={onClickAddBody}
          />
        )
      }
      title={t('client.legal.companyStructure.title')}
    >
      <BodiesInfoCard
        editable={!readOnly}
        items={items}
        onCellClick={onClickEdit}
        onClickArchive={onClickArchive}
        onClickDelete={onClickDelete}
        onClickEdit={onClickEdit}
      />

      <DialogDeleteAndReasonChange
        deleteLoading={dialogLoading}
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        onDelete={onDelete}
      />

      {/** Archive dialog */}
      <DialogReasonChange
        isOpen={isOpenArchiveDialog}
        submitLoading={dialogLoading}
        onClose={onCloseArchiveDialog}
        onSave={onArchiveAndDearchive}
      />

      <BodyDialog
        body={activeBodyItem}
        editable={!readOnly}
        isOpen={isOpenBodyDialog}
        submitLoading={dialogLoading}
        onClose={onCloseBodyDialog}
        onSubmit={handleBodySubmit}
      />

      {/** Error dialog */}
      <ConfirmationDialog
        headerCentered
        hideBackButton
        confirmButtonLabel={t('common.clearly')}
        isOpen={isOpenErrorDialog}
        maxWidth="md"
        subtitle={error?.subtitle}
        title={error?.title ?? ''}
        onClose={onCloseErrorDialog}
        onConfirm={onCloseErrorDialog}
      />
    </Section>
  );
};
